import jwtDecode from 'jwt-decode'
import { REPORT_STATUSES } from '../app/reports/constants'
import moment from 'moment'
import {
    DEFAULT_BAIT_SETTINGS,
    DEFAULT_ROLES,
    DELIGHTED_TAG_ID,
    DEVICES_BATTERY_STATUS_MAX,
    DEVICE_TYPE,
    FIRMWARE_VERSION_NO_UPDATE,
    FIRMWARE_VERSION_WITH_PROPER_CATCH_DETECTION,
    FIRMWARE_VERSION_WITH_WIFI_RSSI,
    LOCALE_MAP_REVERSED,
    LOCAL_DATA_KEY,
    PCB_REVISIONS_BEFORE_CATCH_DETECTION,
    PCB_REVISION_WITH_ADHOC_CATCH_DETECTION,
    PCB_REVISION_WITH_PROPER_CATCH_DETECTION,
    SYSTEM_COMPONENTS_IDS
} from '../app/root/constants'
import { isNull } from 'lodash'
import semver from 'semver'

export const getInitialsFromName = name => {
    const DEFAULT_USER_NAME = 'UN'
    try {
        const result = name
            .split(' ')
            .reduce((initials, namePart) => {
                return initials + namePart.charAt(0).toUpperCase()
            }, '')
            .substr(0, 2)
        return result
    } catch (ex) {
        return DEFAULT_USER_NAME
    }
}

/**
 * Supports string syntax of nested properties
 * @param {Object} obj Object to get value from
 * @param {String} key Object key or chain of nested keys divided by . ("key" or "key.nestedKey", etc.)
 */
export const getObjValueByKey = (obj, key) => {
    if (!obj) {
        return
    }

    const keys = key.split('.')
    const currentKey = keys.shift()
    const value = obj[currentKey]

    return keys.length ? getObjValueByKey(value, keys.join('.')) : value
}

/**
 * Sort an array of objects
 * @param  {Array} arr Array to sort
 * @param  {String} key Object key to compare by
 * @param  {String} dataType String, Number, Boolean, Date. default: String
 * @param  {Boolean} asc Order of sort
 * @return {Array} Returns a sorted array
 */
export const sort = (arr, key, dataType, asc) => {
    if (!arr) {
        return []
    }

    const _arr = [...arr]

    return _arr.sort((a, b) => {
        const av = getObjValueByKey(a, key)
        const bv = getObjValueByKey(b, key)
        let res

        /* prettier-ignore */
        switch (dataType) {
            case 'number':
                res = Number(av) - Number(bv)
                break
            case 'boolean':
                res = Boolean(av) - Boolean(bv)
                break
            case 'date':
                res = new Date(av) - new Date(bv)
                break
            default:
                res = (av > bv && 1) || (av < bv && -1) || 0
        }

        return asc ? res : res * -1
    })
}

export const decodeJwt = token => {
    return jwtDecode(token)
}

export const assignInterceptors = (axios, token) => {
    if (token) {
        axios.defaults.headers.common['Authorization'] = token
    } else {
        delete axios.defaults.headers.common['Authorization']
    }
}

export const isEmailString = str => {
    /* eslint-disable */
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(str).toLowerCase())
    /* eslint-enable */
}

export const getNewUser = (roleId, customerId, roleCode, customerName) => {
    return {
        email: '',
        name: 'New user',
        roles: [
            {
                id: roleId,
                code: roleCode,
                customer_id: customerId,
                customer_name: customerName
            }
        ],
        phone_number: null,
        mail_notification_enabled: false,
        push_notification_enabled: false,
        sms_notification_enabled: false,
        is_original: true,
        language: LOCALE_MAP_REVERSED.de,
        customer_id: customerId
    }
}

export const getNewRole = customerId => {
    return {
        name: 'New role',
        customer_id: customerId,
        code: '___',
        components: Object.keys(SYSTEM_COMPONENTS_IDS).reduce(
            (acc, componentKey) => ({
                [componentKey]: {
                    create: false,
                    read: false,
                    update: false,
                    delete: false
                },
                ...acc
            }),
            {}
        )
    }
}

export const generatePassword = (length = 10) => {
    const getRandomElement = arr => {
        const rand = Math.floor(Math.random() * arr.length)
        return arr[rand]
    }

    const uppercase = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z'
    ]
    const lowercase = [
        'a',
        'b',
        'c',
        'd',
        'e',
        'f',
        'g',
        'h',
        'i',
        'j',
        'k',
        'l',
        'm',
        'n',
        'o',
        'p',
        'q',
        'r',
        's',
        't',
        'u',
        'v',
        'w',
        'x',
        'y',
        'z'
    ]
    const special = ['!', '@', '#', '$', '%', '^', '&', '*']
    const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']

    const nonSpecial = [...uppercase, ...lowercase, ...numbers]

    let password = ''

    password += getRandomElement(uppercase)
    password += getRandomElement(lowercase)
    password += getRandomElement(special)
    password += getRandomElement(numbers)

    for (let i = 0; i < length - 4; i++) {
        password += getRandomElement(nonSpecial)
    }

    return password
}

export function getNewLocation(data) {
    const { customer_id, owner_id, parent_id, locale } = data
    return {
        id: 'new',
        name: locale.locations.newLocation,
        customer_id: customer_id,
        owner_id: owner_id,
        parent_id: parent_id,
        address: null
    }
}

export const buildArrayQueryString = (arr, key) => {
    return arr.reduce((query, item, idx) => {
        if (idx === 0) {
            query += `?${key}=${item}`
        } else {
            query += `&${key}=${item}`
        }
        return query
    }, '')
}

export const getReportStatus = report => {
    switch (true) {
        case /* report.is_infested &&  */ report.report_type == '14':
            return REPORT_STATUSES.infested
        case report.report_type == '2':
        case ['14', '16', '19', '22', '23', '24'].includes(report.report_type):
            return REPORT_STATUSES.critical

        case ['5'].includes(report.report_type):
            return REPORT_STATUSES.down

        case ['20'].includes(report.report_type):
            return REPORT_STATUSES.catch

        case ['4', '7', '10', '13', '18', '17', '15', '21'].includes(
            report.report_type
        ):
            return REPORT_STATUSES.warning

        case ['0', '1', '3', '6', '8', '9', '11', '12', '25'].includes(
            report.report_type
        ):
            return REPORT_STATUSES.info

        default:
            return REPORT_STATUSES.info
    }
}

export const getFormattedDate = dbDate => {
    if (!dbDate) return ''
    const dateTimeFormat = 'DD-MM-YYYY HH:mm:ss'
    const utcTime = moment(dbDate).utc()
    return moment(utcTime)
        .local()
        .format(dateTimeFormat)
}

export const getUserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (ex) {
        return undefined
    }
}

export const getFormattedTime = dbDate => {
    if (!dbDate) return ''
    const dateTimeFormat = 'HH:mm:ss'
    const utcTime = moment(dbDate).utc()
    return moment(utcTime)
        .local()
        .format(dateTimeFormat)
}

export const getFormattedShortDate = dbDate => {
    if (!dbDate) return ''
    const dateTimeFormat = 'DD-MM-YYYY'
    const utcTime = moment(dbDate).utc()
    return moment(utcTime)
        .local()
        .format(dateTimeFormat)
}

export const isNil = val => val === null || val === undefined

export const SHIFT_BAR_WIDTH = 4

export const defaultGetDepthShift = depth => {
    const parsedDepth = depth - 1
    const shift = parsedDepth === -1 ? 0 : parsedDepth * 10 + 20
    return Math.max(shift - SHIFT_BAR_WIDTH, 0)
}

export const isEmptyFused = fused => {
    return !fused || !fused._docs || fused._docs.length === 0
}

export const isEmptyList = list => {
    return !list || list.length === 0
}

export const getBatteryPercentage = value => {
    if (!value) return 0
    return Math.round((value / DEVICES_BATTERY_STATUS_MAX) * 100)
}

export function getReportTypeText(report, locale) {
    try {
        if (!report) return ''
        const deviceType = report.data?.device_type
        if (
            deviceType === DEVICE_TYPE['jerry-bait-rat'] &&
            Number(report.report_type) === 3
        ) {
            return locale.reports.reportTypes['3-bait']
        }
        return locale.reports.reportTypes[report.report_type]
    } catch (ex) {
        return ''
    }
}

export function getReportTypeDescriptionText(report, locale) {
    try {
        if (!report) return ''
        if (report.report_type === undefined) {
            return ''
        }
        if (report.report_type === null) {
            return locale.reports.reportTypesDescription[12]
        }
        const reportType = Number(report.report_type)
        const deviceType = report.data?.device_type
        if (deviceType === DEVICE_TYPE['jerry-bait-rat'] && reportType === 3) {
            return locale.reports.reportTypesDescription['3-bait']
        }
        return locale.reports.reportTypesDescription[report.report_type]
    } catch (ex) {
        return ''
    }
}

export function getDeviceShortStatusText(device, locale) {
    try {
        if (!device) return ''
        if (device.last_report_type === undefined) return ''
        if (device.last_report_type === null)
            return locale.devices.deviceLastReportTypeShortDescription[12]
        const lastReportType = Number(device.last_report_type)
        if (
            device.device_type === DEVICE_TYPE['jerry-bait-rat'] &&
            lastReportType === 3
        ) {
            return locale.devices.deviceLastReportTypeShortDescription['3-bait']
        }
        return locale.devices.deviceLastReportTypeShortDescription[
            lastReportType
        ]
    } catch (ex) {
        return ''
    }
}

export const getOneMonthAgo = () => {
    const unixMonthAgo = moment()
        .subtract(1, 'month')
        .unix()
    return moment.unix(unixMonthAgo).format('YYYY-MM-DD')
}

export const getRoleName = (role, locale) => {
    if (!role || !role.code || !role.name || !locale) return ''
    const isDefaultRole =
        DEFAULT_ROLES.hasOwnProperty(role.code) &&
        locale.userManagement.defaultRoles.hasOwnProperty(role.code)
    return isDefaultRole
        ? locale.userManagement.defaultRoles[role.code]
        : role.name
}

export const isWithWifiStrengthIndicator = device => {
    if (!device) return false
    if (!device.version || device.version === FIRMWARE_VERSION_NO_UPDATE)
        return false
    try {
        if (device.device_type === DEVICE_TYPE.tom) {
            return true
        }

        const cleanFirmwareVersion = semver.clean(device.version)
        if (
            typeof cleanFirmwareVersion === 'string' &&
            semver.gte(cleanFirmwareVersion, FIRMWARE_VERSION_WITH_WIFI_RSSI)
        ) {
            return true
        }

        return false

        // // const withWifiRSSI = deviceType === DEVICE_TYPE.tom ? TOM_FIRMWARE_VERSION_WITH_WIFI_RSSI : FIRMWARE_VERSION_WITH_WIFI_RSSI
        // const withWifiRSSI = FIRMWARE_VERSION_WITH_WIFI_RSSI
        // const rssiFirmwareVersionArray = withWifiRSSI
        //     .split('.')
        //     .map(versionStrInt => Number(versionStrInt))
        // const firmwareVersionArray = firmwareVersion
        //     .split('.')
        //     .map(versionStr => {
        //         const versionStrInt = versionStr.replace(/\D/g, '')
        //         return Number(versionStrInt)
        //     })
        // for (let i = 0; i < firmwareVersionArray.length; i++) {
        //     if (firmwareVersionArray[i] === rssiFirmwareVersionArray[i]) {
        //         continue
        //     } else if (firmwareVersionArray[i] > rssiFirmwareVersionArray[i]) {
        //         return true
        //     } else if (firmwareVersionArray[i] < rssiFirmwareVersionArray[i]) {
        //         return false
        //     }
        // }

        // return true
    } catch (ex) {
        return false
    }
}

export const getReportTypeTooltipText = (locale, report) => {
    try {
        const reportTypeLabel = getReportTypeText(report, locale) || null
        const reportTypeDescription =
            getReportTypeDescriptionText(report, locale) || ''
        const reportTypeTooltipText =
            reportTypeLabel === null
                ? null
                : `${reportTypeLabel}: ${reportTypeDescription}`
        return reportTypeTooltipText
    } catch (ex) {
        return null
    }
}

export const getDeviceStatusTooltipText = (locale, report) => {
    try {
        if (report === undefined) return null
        if (isNull(report.report_type)) {
            return locale.devices.deviceIsInactive
        }
        const statusText = getReportTypeDescriptionText(report, locale)
        return statusText
    } catch (ex) {
        return null
    }
}

export const getDateTimeOfEmailSent = (locale, date, time) => {
    try {
        let sentAtTooltipText = locale.reports.sentAt + date + ' - ' + time
        return sentAtTooltipText
    } catch (ex) {
        return null
    }
}

export const downloadFile = (blob, fileType, fileName) => {
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.target = '_blank'
    if (fileType !== 'pdf') {
        anchor.download = fileName
    }
    document.body.appendChild(anchor)
    anchor.click(),
        () => {
            URL.revokeObjectURL(objectUrl)
        }
}

export const prepareReportsFiltersAsParams = filters => {
    const params = filters ? { ...filters } : filters
    if (params?.from)
        params.from = moment.unix(params.from).format('YYYY-MM-DD')
    if (params?.to)
        params.to = moment
            .unix(params.to)
            .add(1, 'days')
            .format('YYYY-MM-DD')
    return params
}

export const getLocationDevicesAmount = location => {
    if (!location) return null
    const devicesAmountKeys = [
        'devices_active',
        'devices_critical',
        'devices_inactive',
        'devices_warning'
    ]
    if (devicesAmountKeys.some(k => !location.hasOwnProperty(k))) return null
    return devicesAmountKeys.reduce((acc, k) => {
        acc += location[k]
        return acc
    }, 0)
}

export const composeFileName = (exportType, fileType) => {
    return `traplinked-${exportType}-${moment().format(
        'DD-MM-YYYY(HH-mm)'
    )}.${fileType}`
}

export const isFiltersTimeRangeExceededForExport = filters => {
    const to = filters.to ? moment.unix(filters.to) : moment()
    const from = moment.unix(filters.from)
    const diff = to.diff(from, 'months')
    return diff >= 3
}

export function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

/* eslint-disable */
export function initDelightedSurvey(email, name, customerName) {
    let userEmail = email,
        userName = name,
        userCustomerName = customerName
    if (!email && !name && !customerName) {
        try {
            const user = localStorage.getItem(LOCAL_DATA_KEY)
                ? JSON.parse(localStorage.getItem(LOCAL_DATA_KEY)).user
                : null
            if (!user) return
            userEmail = user.email
            userName = user.name
            userCustomerName = user.original_customer_name
        } catch (ex) {
            return
        }
    }
    const existingScript = document.getElementById(DELIGHTED_TAG_ID)
    if (existingScript) return

    const scriptEl = document.createElement('script')
    scriptEl.type = 'text/javascript'
    scriptEl.id = DELIGHTED_TAG_ID

    scriptEl.innerHTML = `
        !function(e,t,r,n){if(!e[n]){for(var a=e[n]=[],i=["survey","reset","config","init","set","get","event","identify","track","page","screen","group","alias"],s=0;s<i.length;s++){var c=i[s];a[c]=a[c]||function(e){return function(){var t=Array.prototype.slice.call(arguments);a.push([e,t])}}(c)}a.SNIPPET_VERSION="1.0.1";var o=t.createElement("script");o.type="text/javascript",o.async=!0,o.src="https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/"+r+"/"+n+".js";var p=t.getElementsByTagName("script")[0];p.parentNode.insertBefore(o,p)}}(window,document,"qmJwqIZZGbFhG7Ud","delighted");

        delighted.survey({
            email: '${userEmail}',
            name: '${userName}',
            createdAt: new Date().toISOString(),
            properties: {
                company: '${userCustomerName}'
            }
        })
    `

    document.getElementsByTagName('head')[0].appendChild(scriptEl)
}

export function detachDelightedSurvey() {
    const existingScript = document.getElementById(DELIGHTED_TAG_ID)
    if (!existingScript) return
    existingScript.parentNode.removeChild(existingScript)
}

export function getOS() {
    const userAgent = window.navigator.userAgent,
        platform =
            window.navigator?.userAgentData?.platform ||
            window.navigator.platform,
        macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
        windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
        iosPlatforms = ['iPhone', 'iPad', 'iPod']
    let os = null

    if (macosPlatforms.indexOf(platform) !== -1) {
        os = 'Mac OS'
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = 'iOS'
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Windows'
    } else if (/Android/.test(userAgent)) {
        os = 'Android'
    } else if (/Linux/.test(platform)) {
        os = 'Linux'
    }

    return os
}

export function isPcbRevisionWithCatchDetectionAdHoc(pcbRevision) {
    if (!pcbRevision) return false
    return pcbRevision === PCB_REVISION_WITH_ADHOC_CATCH_DETECTION
}

export function validateIccid(iccid) {
    const regex = /^[0-9]{19,20}$/
    return regex.test(iccid)
}

export const getLocalTimestampFromDbDate = dbDate => {
    const utcTime = moment(dbDate).utc()
    return moment(utcTime)
        .local()
        .toDate()
}

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export function prepareNewEscalationLevel(currentLevels, localeCode) {
    const previousLevel = currentLevels[currentLevels.length - 1]
    let daysUntilAlert = null
    let hoursUntilAlert = null

    if (previousLevel) {
        const isNewDay =
            previousLevel.hoursUntilAlert !== null &&
            previousLevel.hoursUntilAlert !== undefined &&
            previousLevel.hoursUntilAlert >= 22
        daysUntilAlert = isNewDay
            ? previousLevel.daysUntilAlert + 1
            : previousLevel.daysUntilAlert
        hoursUntilAlert = isNewDay ? 0 : previousLevel.hoursUntilAlert + 2
    } else {
        daysUntilAlert = 0
        hoursUntilAlert = 2
    }

    return {
        level: previousLevel?.level + 1 || 1,
        email: '',
        message: '',
        daysUntilAlert,
        locale: localeCode,
        hoursUntilAlert,
        reportTypes: []
    }
}

export function getEscLevelTimeMinsFromList(escLevels, index) {
    const previousLevel = escLevels[index - 1]
    const currentLevel = escLevels[index]
    if (!previousLevel) {
        return {
            daysUntilAlertMin: 0,
            hoursUntilAlertMin: currentLevel.daysUntilAlert > 0 ? 0 : 2
        }
    } else {
        const isNewDay =
            previousLevel.hoursUntilAlert !== null &&
            previousLevel.hoursUntilAlert !== undefined &&
            previousLevel.hoursUntilAlert >= 22
        return {
            daysUntilAlertMin: isNewDay
                ? previousLevel.daysUntilAlert + 1
                : previousLevel.daysUntilAlert,
            hoursUntilAlertMin:
                isNewDay ||
                (currentLevel.daysUntilAlert > previousLevel.daysUntilAlert &&
                    currentLevel.daysUntilAlert > 0)
                    ? 0
                    : previousLevel.hoursUntilAlert + 2
        }
    }
}

export function isFirmwareWithProperCatchDetection(version) {
    if (!version) return false
    try {
        return semver.gte(
            semver.coerce(version),
            FIRMWARE_VERSION_WITH_PROPER_CATCH_DETECTION
        )
    } catch (ex) {
        return false
    }
}

export function isPcbRevisionWithProperCatchDetection(pcbRevision) {
    if (
        !PCB_REVISIONS_BEFORE_CATCH_DETECTION.hasOwnProperty(pcbRevision) &&
        pcbRevision !== PCB_REVISION_WITH_ADHOC_CATCH_DETECTION
    ) {
        try {
            if (
                semver.gte(
                    semver.coerce(pcbRevision),
                    PCB_REVISION_WITH_PROPER_CATCH_DETECTION
                )
            ) {
                return true
            }
        } catch (ex) {
            return false
        }
    }
    return false
}

export function isJerry3(firmwareVersion, pcbRevision) {
    if (!firmwareVersion || !pcbRevision) return false
    try {
        if (isPcbRevisionWithCatchDetectionAdHoc(pcbRevision)) {
            return true
        } else if (
            isPcbRevisionWithProperCatchDetection(pcbRevision) &&
            isFirmwareWithProperCatchDetection(firmwareVersion)
        ) {
            return true
        }
    } catch (ex) {
        return false
    }
}

export function getBaitActivityThresholdDataFromString(thresholdString) {
    const dataArr = thresholdString.split('_')
    return {
        threshold: parseInt(dataArr[0]),
        unitsAmount: parseInt(dataArr[1]),
        unit: dataArr[2]
    }
}

export function createDefaultBaitSettings() {
    return { ...DEFAULT_BAIT_SETTINGS }
}

/* eslint-enable */
